import React from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import SEO from '../components/SEO';
import CTASection from '../components/CTASection';
import TrustSignals from '../components/TrustSignals';
import { 
  CheckCircle2, 
  Briefcase,
  Home,
  Key,
  MapPin,
  Star,
  ArrowRight,
  Building
} from 'lucide-react';
import {
  combineSchemas,
  generateLocalBusinessSchema,
  generateServiceSchema,
  generateBreadcrumbSchema,
  generateWebPageSchema,
  generateFAQSchema
} from '../utils/schemaGenerator';

const MelbournePage = () => {
  const services = [
    {
      icon: Home,
      title: "Recurring House Cleaning",
      description: "Professional cleaning for Melbourne's busy households. Weekly, bi-weekly, or monthly service with the same trusted team.",
      link: "/services/house-cleaning"
    },
    {
      icon: Key,
      title: "Real Estate & Listing Prep",
      description: "Helping Melbourne Realtors showcase properties at their best. Pre-listing deep cleans and open house preparation.",
      link: "/services/move-in-move-out-cleaning"
    },
    {
      icon: Building,
      title: "Property Management Turnover",
      description: "Fast, reliable turnover cleaning for property managers handling Melbourne rentals and multi-unit properties.",
      link: "/services/commercial"
    },
    {
      icon: Briefcase,
      title: "Move-In / Move-Out Cleaning",
      description: "Inspection-ready cleaning for Melbourne renters, homeowners, and landlords during transitions.",
      link: "/services/move-in-move-out-cleaning"
    }
  ];

  const faqs = [
    {
      question: "Do you work with Melbourne Realtors and property managers?",
      answer: "Yes. We work with Realtors throughout Melbourne preparing listings for the market, staging homes for open houses, and handling post-sale cleaning. We also partner with property managers for turnover cleaning, vacant unit prep, and ongoing maintenance."
    },
    {
      question: "Which parts of Melbourne do you serve?",
      answer: "We serve all of Melbourne—from Downtown Melbourne and Eau Gallie to Suntree, Lake Washington, and West Melbourne. Whether you're near the beaches, the medical district, or the residential neighborhoods inland, we've got you covered."
    },
    {
      question: "Can you handle same-day or fast-turnaround cleaning for property managers?",
      answer: "Yes. We understand property managers work on tight timelines. While we appreciate advance notice, we do our best to accommodate urgent requests for vacant units, turnovers, and inspection prep."
    },
    {
      question: "What makes Pierce Premier different from other Melbourne cleaning companies?",
      answer: "We're locally owned by Bob and Heather, not a franchise. You'll work directly with us and see the same trusted cleaners every time—no rotating crews. Heather's 15+ years of executive housekeeping experience means we deliver hospitality-level standards to every Melbourne home and property."
    },
    {
      question: "Do you serve both residential and commercial clients in Melbourne?",
      answer: "Yes. We serve Melbourne families with recurring and deep cleaning, as well as Realtors, property managers, landlords, and business owners. Whether it's your home or your rental portfolio, we're here to help."
    }
  ];

  const structuredData = combineSchemas(
    generateWebPageSchema({
      name: "House Cleaning Services in Melbourne, FL | Pierce Premier",
      description: "Trusted house cleaning in Melbourne, FL. Serving residential, Realtors, and property managers. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347.",
      url: "/areas/melbourne"
    }),
    generateBreadcrumbSchema([
      { name: "Home", url: "/" },
      { name: "Service Areas", url: "/areas" },
      { name: "Melbourne" }
    ]),
    generateLocalBusinessSchema(),
    generateServiceSchema({
      serviceName: "House Cleaning Services in Melbourne",
      description: "Professional house cleaning, real estate prep, and property management turnover services in Melbourne, FL."
    }),
    generateFAQSchema(faqs)
  );

  return (
    <>
      <SEO 
        title="House Cleaning Services in Melbourne, FL | Pierce Premier"
        description="Trusted house cleaning in Melbourne, FL. Residential, Realtor, and property management services. Same team every visit, 15+ years experience, fully insured. Call 321-408-1347."
        canonical="/areas/melbourne"
        structuredData={structuredData}
      />
      
      <div className="min-h-screen pt-20">
        {/* Hero Section */}
        <section className="bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 py-20 px-4 sm:px-6 lg:px-8">
          <div className="container mx-auto max-w-7xl">
            <div className="grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <div className="flex items-center gap-2 mb-4">
                  <MapPin className="w-5 h-5 text-indigo-600" />
                  <Badge className="bg-indigo-600 text-white">Brevard&apos;s Hub</Badge>
                </div>
                <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-gray-900 mb-6">
                  House Cleaning Services in Melbourne, FL
                </h1>
                <p className="text-base sm:text-lg text-gray-600 mb-6">
                  Melbourne is Brevard County&apos;s bustling center—home to professionals, families, growing businesses, and a thriving real estate market. Pierce Premier serves this diverse community with reliable, professional cleaning services for homes, Realtors, and property managers who need consistency they can count on.
                </p>
                <p className="text-base sm:text-lg text-gray-600 mb-8">
                  Whether you&apos;re a homeowner in Suntree looking for recurring service, a Realtor preparing a listing in Eau Gallie, or a property manager turning over a unit near Downtown Melbourne, we bring the same commitment to quality and professionalism to every job.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 mb-8">
                  <Link 
                    to="/contact" 
                    className="inline-flex items-center justify-center bg-indigo-600 text-white px-8 py-4 rounded-lg font-semibold hover:bg-indigo-700 transition-colors"
                  >
                    Get Free Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                  <a 
                    href="tel:+1-321-408-1347" 
                    className="inline-flex items-center justify-center border-2 border-indigo-600 text-indigo-600 px-8 py-4 rounded-lg font-semibold hover:bg-indigo-50 transition-colors"
                  >
                    Call: 321-408-1347
                  </a>
                </div>
                <TrustSignals variant="compact" />
              </div>
              <div className="relative">
                <img 
                  src="https://images.unsplash.com/photo-1600607687939-ce8a6c25118c?auto=format&fit=crop&w=800&q=80" 
                  alt="Professional house cleaning services in Melbourne, Florida"
                  className="rounded-2xl shadow-2xl"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Why Melbourne Chooses Pierce Premier */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Why Melbourne Professionals Choose Pierce Premier
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                From busy households to real estate professionals, Melbourne clients value consistency, professionalism, and local accountability.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Star className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Professional Standards</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Heather&apos;s 15+ years of executive housekeeping experience means we bring hospitality-level standards to every Melbourne home and property. We know what quality looks like, and we deliver it consistently.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Briefcase className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Trusted by Realtors & Property Managers</h3>
                  <p className="text-gray-600 leading-relaxed">
                    We work with real estate professionals throughout Melbourne who need reliable cleaning they can count on. Fast turnarounds, inspection-ready results, and direct communication with Bob and Heather.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <Home className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Locally Owned & Accountable</h3>
                  <p className="text-gray-600 leading-relaxed">
                    Pierce Premier is locally owned by Bob and Heather—not a franchise. When you call, you reach us directly. When you need something, we respond. That local accountability matters in Melbourne&apos;s competitive market.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-2">
                <CardContent className="p-8">
                  <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-xl flex items-center justify-center mb-4">
                    <CheckCircle2 className="w-6 h-6 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">Same Team, Every Time</h3>
                  <p className="text-gray-600 leading-relaxed">
                    No rotating crews. Whether you&apos;re a homeowner with recurring service or a property manager with multiple units, you&apos;ll see the same trusted cleaners. They learn your expectations and deliver consistent results.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Services for Melbourne */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-7xl">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                Cleaning Services for Melbourne Homes & Properties
              </h2>
              <p className="text-base sm:text-lg text-gray-600 max-w-3xl mx-auto">
                Residential cleaning, real estate prep, and property management solutions.
              </p>
            </div>

            <div className="grid md:grid-cols-2 gap-8">
              {services.map((service, idx) => {
                const Icon = service.icon;
                return (
                  <Card key={idx} className="border-2 hover:border-indigo-200 transition-colors group">
                    <CardContent className="p-6">
                      <div className="w-14 h-14 bg-gradient-to-br from-indigo-400 to-blue-500 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                        <Icon className="w-7 h-7 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-900 mb-3">{service.title}</h3>
                      <p className="text-gray-600 mb-4 leading-relaxed">{service.description}</p>
                      <Link 
                        to={service.link}
                        className="inline-flex items-center text-indigo-600 font-semibold hover:text-indigo-700"
                      >
                        Learn More
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Link>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          </div>
        </section>

        {/* Melbourne Areas We Serve */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <div className="text-center mb-12">
              <h2 className="text-3xl font-bold text-gray-900 mb-4">
                Melbourne Areas & Communities We Serve
              </h2>
              <p className="text-gray-600 mb-8">
                We serve homes and properties throughout Melbourne, including:
              </p>
            </div>

            <div className="grid sm:grid-cols-2 md:grid-cols-3 gap-4">
              {[
                "Downtown Melbourne",
                "Eau Gallie",
                "Suntree",
                "Lake Washington",
                "West Melbourne",
                "Melbourne Village",
                "Crane Creek",
                "Sherwood Forest",
                "Floridana Beach",
                "Medical District Area",
                "Melbourne Beach Road",
                "Wickham Road Corridor"
              ].map((area, idx) => (
                <div key={idx} className="flex items-center gap-2 p-3 bg-indigo-50 rounded-lg border border-indigo-100">
                  <CheckCircle2 className="w-5 h-5 text-indigo-600 flex-shrink-0" />
                  <span className="text-gray-900 font-medium">{area}</span>
                </div>
              ))}
            </div>

            <div className="mt-8 text-center">
              <p className="text-gray-600 mb-2">
                <span className="font-semibold">ZIP Codes:</span> 32901, 32904, 32934, 32935, 32940, 32951
              </p>
              <p className="text-sm text-gray-500">
                Serving residential and commercial properties across Melbourne
              </p>
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-4xl font-bold text-gray-900 mb-12 text-center">
              Common Questions from Melbourne Clients
            </h2>
            <div className="space-y-6">
              {faqs.map((faq, idx) => (
                <Card key={idx} className="border-2">
                  <CardContent className="p-6">
                    <h3 className="text-xl font-bold text-gray-900 mb-3">{faq.question}</h3>
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </section>

        {/* For Property Managers & Realtors */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
          <div className="container mx-auto max-w-5xl">
            <Card className="border-4 border-indigo-200 shadow-xl">
              <CardContent className="p-8 md:p-12">
                <Briefcase className="w-12 h-12 text-indigo-600 mb-6 mx-auto" />
                <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center">
                  For Melbourne Realtors & Property Managers
                </h2>
                <p className="text-gray-700 leading-relaxed mb-4">
                  We work with real estate professionals throughout Melbourne who need a reliable cleaning partner. Whether you&apos;re preparing a listing for the market, handling post-sale cleaning, or turning over rental units, we deliver inspection-ready results on your timeline.
                </p>
                <p className="text-gray-700 leading-relaxed mb-6">
                  Direct communication with Bob and Heather means faster response times and flexible scheduling. Many Melbourne Realtors and property managers trust Pierce Premier because we understand their business and deliver every time.
                </p>
                <div className="text-center">
                  <Link 
                    to="/contact"
                    className="inline-flex items-center text-indigo-600 font-semibold hover:text-indigo-700 text-lg"
                  >
                    Request Commercial Quote
                    <ArrowRight className="ml-2 w-5 h-5" />
                  </Link>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Testimonial */}
        <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
          <div className="container mx-auto max-w-4xl">
            <Card className="border-4 border-indigo-200 shadow-xl">
              <CardContent className="p-8 md:p-12 text-center">
                <Star className="w-12 h-12 text-yellow-500 fill-yellow-500 mx-auto mb-4" />
                <blockquote className="text-xl md:text-2xl text-gray-700 font-medium mb-6 italic">
                  &quot;We use Pierce Premier for our office cleaning every week. Always on time, always spotless. Highly recommend!&quot;
                </blockquote>
                <p className="text-gray-600 font-semibold">Michael Rodriguez</p>
                <p className="text-gray-500 text-sm">Melbourne, FL Business Owner</p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* CTA Section */}
        <CTASection 
          title="Ready to Work with Pierce Premier?"
          description="Serving Melbourne homeowners, Realtors, and property managers with professional cleaning they can count on."
          primaryButtonText="Get Your Free Quote"
        />
      </div>
    </>
  );
};

export default MelbournePage;
